import { jsx, Fragment } from '@emotion/react/jsx-runtime';
import { cx } from '@emotion/css';
import { useTheme } from '@emotion/react';
import { resolveColorAlias, useCss, parseColor, cloneElementWithCss } from '@nettbureau/styling';
import { rgba } from 'polished';
import { useContext, Children, isValidElement, createContext } from 'react';
import { createComponent } from '../system/factories.js';
import '@react-aria/utils';
import 'ramda';
import 'react-aria';
import { useColorMode } from '../theming/theme-provider.js';

/**
 * Context providing information about whether a focus ring is active in the current tree.
 */ const FocusRingContext = /*#__PURE__*/ createContext(false);
/**
 * The focus ring component is responsible for dynamically styling its children based on focus
 * state. It applies styles programmatically based on the `focused` prop.
 */ const FocusRing = createComponent((props)=>{
    const { children, className, color = 'blue', focused, ...otherProps } = props;
    const focusRingActive = useContext(FocusRingContext);
    const theme = useTheme();
    const colorMode = useColorMode();
    const colorToken = resolveColorAlias(theme, color);
    const focusColor = colorToken === 'gray' ? 'blue' : colorToken;
    const css = useCss(({ theme })=>[
            {
                outline: `0 solid transparent`
            },
            focused && {
                outline: `${theme.scales.base}px solid ${rgba(parseColor(theme, focusColor, colorMode === 'light' ? 400 : 200), theme.opacities.weak)}`
            }
        ]);
    if (focusRingActive) return /*#__PURE__*/ jsx(Fragment, {
        children: children
    });
    const child = Children.only(children);
    return /*#__PURE__*/ jsx(FocusRingContext.Provider, {
        value: focused,
        children: /*#__PURE__*/ isValidElement(child) ? cloneElementWithCss(child, {
            ...otherProps,
            className: child.props.className ? cx(child.props.className, className) : className,
            css
        }) : null
    });
});

export { FocusRing };
//# sourceMappingURL=focus-ring.js.map
